import { useState } from "react";
import { useDateFormat } from '@/contexts/DateFormatContext';
import { trpc } from "../lib/trpc";
import { useTranslation } from "../i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Edit, Trash2, Eye, Image, QrCode, Video, ArrowLeft, Sparkles, Folder } from "lucide-react";
import { useLocation } from "wouter";

export default function Projects() {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const { formatDate } = useDateFormat();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
  });

  const { data: projects, isLoading, refetch } = trpc.projects.list.useQuery();
  const createMutation = trpc.projects.create.useMutation({
    onSuccess: () => {
      refetch();
      setCreateDialogOpen(false);
      setFormData({ title: "", description: "" });
    },
  });
  const updateMutation = trpc.projects.update.useMutation({
    onSuccess: () => {
      refetch();
      setEditDialogOpen(false);
    },
  });
  const deleteMutation = trpc.projects.delete.useMutation({
    onSuccess: () => {
      refetch();
      setDeleteDialogOpen(false);
    },
  });
  const updateStatusMutation = trpc.projects.updateStatus.useMutation({
    onSuccess: () => refetch(),
  });

  const handleCreate = () => {
    createMutation.mutate(formData);
  };

  const handleEdit = () => {
    if (selectedProject) {
      updateMutation.mutate({
        id: selectedProject.id,
        ...formData,
      });
    }
  };

  const handleDelete = () => {
    if (selectedProject) {
      deleteMutation.mutate({ id: selectedProject.id });
    }
  };
  // Removed erroneous inline import

  const handleStatusChange = (projectId: number, status: "draft" | "active" | "archived") => {
    updateStatusMutation.mutate({ id: projectId, status });
  };

  const openEditDialog = (project: any) => {
    setSelectedProject(project);
    setFormData({
      title: project.title,
      description: project.description || "",
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (project: any) => {
    setSelectedProject(project);
    setDeleteDialogOpen(true);
  };

  if (isLoading) {
    return <div className="p-8">{t.common.loading}</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Header with Back Button */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/")}
              className="gap-2 hover:bg-purple-50 dark:hover:bg-purple-950/30 hover:text-purple-600 dark:hover:text-purple-400 hover:border-purple-300 dark:hover:border-purple-700 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              {t.projects.backToDashboard}
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto p-8">
        {/* Hero Section */}
        <div className="mb-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
                <Folder className="h-9 w-9 text-purple-600" />
                <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  {t.projects.title}
                </span>
              </h1>
              <p className="text-gray-600 flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                {projects?.length === 1 
                  ? t.projects.projectsCount.split('|')[0].trim().replace('{count}', String(projects.length))
                  : t.projects.projectsCount.split('|')[1].trim().replace('{count}', String(projects?.length || 0))
                }
              </p>
            </div>
            <Button 
              onClick={() => setCreateDialogOpen(true)}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all"
            >
          <Plus className="mr-2 h-4 w-4" />
          {t.projects.createProject}
        </Button>
          </div>
        </div>

        {projects && projects.length === 0 ? (
          <Card className="border-2 border-dashed border-gray-300 bg-white/50 backdrop-blur-sm">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mb-4">
                <Plus className="h-8 w-8 text-purple-600" />
              </div>
              <p className="text-gray-600 mb-4 text-lg">{t.projects.noProjects}</p>
              <Button 
                onClick={() => setCreateDialogOpen(true)}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                <Plus className="mr-2 h-4 w-4" />
                {t.projects.createFirstProject}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects?.map((project) => (
              <div
                key={project.id}
                className="rounded-xl bg-gradient-to-r from-purple-500/30 via-pink-500/30 to-blue-500/30 p-[1px] transition-all hover:from-purple-500/50 hover:via-pink-500/50 hover:to-blue-500/50"
              >
              <Card 
                className="rounded-[14px] hover:shadow-2xl transition-all duration-300 bg-white/80 dark:bg-gray-900/60 backdrop-blur-sm border-gray-200 hover:scale-[1.02]"
              >
              <CardHeader>
                <div className="flex justify-between items-start gap-3">
                  <div className="flex items-center gap-3">
                    {project.avatarImage?.id && (
                      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900 dark:to-pink-900">
                        <img 
                          src={`/api/image-proxy/${project.avatarImage.id}/thumbnail`}
                          alt={project.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div>
                      <CardTitle>{project.title}</CardTitle>
                      <CardDescription>#{project.projectNumber}</CardDescription>
                    </div>
                  </div>
                  <Select
                    value={project.status}
                    onValueChange={(value: any) => handleStatusChange(project.id, value)}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">{t.projects.draft}</SelectItem>
                      <SelectItem value="active">{t.projects.active}</SelectItem>
                      <SelectItem value="archived">{t.projects.archived}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {project.description || t.projects.noDescription}
                </p>
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t.projects.createdAt}:</span>
                    <span>{formatDate(project.createdAt)}</span>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/projects/${project.id}/images`)}
                  className="gap-2 hover:bg-purple-50 dark:hover:bg-purple-950/30 hover:text-purple-600 dark:hover:text-purple-400 hover:border-purple-300 dark:hover:border-purple-700 transition-colors"
                >
                  <Image className="h-4 w-4" />
                  {t.images.title}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/projects/${project.id}/videos`)}
                  className="gap-2 hover:bg-purple-50 dark:hover:bg-purple-950/30 hover:text-purple-600 dark:hover:text-purple-400 hover:border-purple-300 dark:hover:border-purple-700 transition-colors"
                >
                  <Video className="h-4 w-4" />
                  {t.projects.viewVideos}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/projects/${project.id}/codes`)}
                  className="gap-2 hover:bg-purple-50 dark:hover:bg-purple-950/30 hover:text-purple-600 dark:hover:text-purple-400 hover:border-purple-300 dark:hover:border-purple-700 transition-colors"
                >
                  <QrCode className="h-4 w-4" />
                  {t.accessCodes.title}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => openEditDialog(project)}
                  className="hover:bg-purple-50 dark:hover:bg-purple-950/30 hover:text-purple-600 dark:hover:text-purple-400 hover:border-purple-300 dark:hover:border-purple-700 transition-colors"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openDeleteDialog(project)}
                  className="hover:bg-red-50 dark:hover:bg-red-950/30 hover:text-red-600 dark:hover:text-red-400 hover:border-red-300 dark:hover:border-red-700 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </CardFooter>
            </Card>
            </div>
           ))}
          </div>
        )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.projects.createProject}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-3">
              <Label htmlFor="title">{t.projects.projectName}</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={t.projects.projectName}
              />
            </div>
            <div className="space-y-3">
              <Label htmlFor="description">{t.projects.projectDescription}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t.projects.projectDescription}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button onClick={handleCreate} disabled={!formData.title || createMutation.isPending}>
              {createMutation.isPending ? t.common.loading : t.common.create}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.projects.editProject}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-3">
              <Label htmlFor="edit-title">{t.projects.projectName}</Label>
              <Input
                id="edit-title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>
            <div className="space-y-3">
              <Label htmlFor="edit-description">{t.projects.projectDescription}</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button onClick={handleEdit} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? t.common.loading : t.common.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.projects.deleteProject}</DialogTitle>
            <DialogDescription>{t.projects.confirmDelete}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? t.common.loading : t.common.delete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
